import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  People as PeopleIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import planApi from '../utils/planApi';
import PlanForm from '../components/PlanForm';
import PlanImpactDialog from '../components/PlanImpactDialog';
import { DataTable, StatusBadge } from '../components/ui';

const PlanListPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openImpactDialog, setOpenImpactDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [planImpacts, setPlanImpacts] = useState({}); // Store impact data for all plans
  const [loadingImpacts, setLoadingImpacts] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const plansData = await planApi.getAllPlans();
      setPlans(plansData);
      
      // Fetch impact data for all plans in parallel
      await fetchPlanImpacts(plansData);
      
      setError('');
    } catch (err) {
      setError(t('admin:plans.errors.fetchFailed'));
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanImpacts = async (plansData) => {
    try {
      setLoadingImpacts(true);
      const impactPromises = plansData.map(plan => 
        planApi.getPlanImpactSummary(plan._id).catch(err => {
          console.warn(`Failed to get impact for plan ${plan._id}:`, err);
          return {
            hasImpact: false,
            totalActiveUsers: 0,
            pendingRegistrations: 0,
            canSafelyEdit: true
          };
        })
      );
      
      const impacts = await Promise.all(impactPromises);
      const impactMap = {};
      
      plansData.forEach((plan, index) => {
        impactMap[plan._id] = impacts[index];
      });
      
      setPlanImpacts(impactMap);
    } catch (err) {
      console.error('Error fetching plan impacts:', err);
    } finally {
      setLoadingImpacts(false);
    }
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setFormMode('create');
    setOpenForm(true);
  };

  const handleEditPlan = (plan) => {
    const planImpact = planImpacts[plan._id];
    
    if (planImpact && !planImpact.canSafelyEdit) {
      setSelectedPlan(plan);
      setOpenImpactDialog(true);
    } else {
      setSelectedPlan(plan);
      setFormMode('edit');
      setOpenForm(true);
    }
  };

  const handleProceedWithEdit = () => {
    setOpenImpactDialog(false);
    setFormMode('edit');
    setOpenForm(true);
  };

  const handleDeletePlan = (plan) => {
    setSelectedPlan(plan);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/plans/${selectedPlan._id}`);
      setSuccess(t('admin:plans.messages.deleteSuccess'));
      setOpenDeleteDialog(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (err) {
      setError(t('admin:plans.errors.deleteFailed'));
      console.error('Error deleting plan:', err);
    }
  };

  const handleFormSubmit = async (planData) => {
    try {
      if (formMode === 'create') {
        await planApi.createPlan(planData);
        setSuccess(t('admin:plans.messages.createSuccess'));
      } else {
        const response = await planApi.updatePlan(selectedPlan._id, planData);
        
        let successMessage = t('admin:plans.messages.updateSuccess');
        if (response.warning && response.activeUsersCount > 0) {
          successMessage += ` ⚠️ ${response.activeUsersCount} active users are protected by plan snapshots.`;
        }
        setSuccess(successMessage);
        
        if (response.warning) {
          setTimeout(() => {
            setError('Notice: ' + response.warning);
            setTimeout(() => setError(''), 8000);
          }, 2000);
        }
      }
      setOpenForm(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (err) {
      setError(t('admin:plans.errors.saveFailed'));
      console.error(`Error ${formMode}ing plan:`, err);
    }
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const formatDuration = (duration) => {
    if (duration === 1) return t('admin:plans.duration.oneDay');
    if (duration < 30) return t('admin:plans.duration.days', { count: duration });
    if (duration === 30) return t('admin:plans.duration.oneMonth');
    if (duration < 365) return t('admin:plans.duration.months', { count: Math.round(duration / 30) });
    return t('admin:plans.duration.years', { count: Math.round(duration / 365) });
  };

  // Define table columns
  const columns = [
    {
      key: 'name',
      title: t('common:labels.name'),
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div style={{ fontWeight: 'bold' }}>
          {row.name}
        </div>
      )
    },
    {
      key: 'description',
      title: t('common:labels.description'),
      render: (value, row) => (
        <div style={{ maxWidth: '200px' }}>
          {row.description.length > 50
            ? `${row.description.substring(0, 50)}...`
            : row.description}
        </div>
      )
    },
    {
      key: 'price',
      title: t('admin:plans.table.headers.price'),
      sortable: true,
      render: (value, row) => (
        <div style={{ fontWeight: '500', color: '#1976d2' }}>
          {formatPrice(row.price)}
        </div>
      )
    },
    {
      key: 'duration',
      title: t('admin:plans.table.headers.duration'),
      sortable: true,
      render: (value, row) => formatDuration(row.duration)
    },
    {
      key: 'features',
      title: t('admin:plans.table.headers.features'),
      render: (value, row) => (
        <div>
          {t('admin:plans.table.featuresCount', { count: row.features.length })}
        </div>
      )
    },
    {
      key: 'memberTypeConfig',
      title: t('admin:plans.table.headers.memberTypeAccess'),
      render: (value, row) => (
        <div>
          {row.memberTypeConfig && row.memberTypeConfig.length > 0 ? (
            <div>
              {t('admin:plans.table.typesConfigured', { count: row.memberTypeConfig.length })}
              <br />
              <small style={{ color: '#666' }}>
                {t('admin:plans.table.defaultAccess')}: {row.defaultEligible ? t('admin:plans.table.allowed') : t('admin:plans.table.restricted')}
              </small>
            </div>
          ) : (
            <div style={{ color: '#666' }}>
              {t('admin:plans.table.allMemberTypes')}: {row.defaultEligible ? t('admin:plans.table.allowed') : t('admin:plans.table.restricted')}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'impact',
      title: t('admin:plans.table.headers.userImpact'),
      render: (value, row) => {
        const impact = planImpacts[row._id];
        if (!impact || loadingImpacts) {
          return <div style={{ color: '#666' }}>{t('common:loading.default')}</div>;
        }
        
        if (impact.hasImpact) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Tooltip title={`${impact.totalActiveUsers} active users, ${impact.pendingRegistrations} pending registrations`}>
                <Chip
                  icon={<PeopleIcon />}
                  label={t('admin:plans.table.usersCount', { count: impact.totalActiveUsers + impact.pendingRegistrations })}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            </div>
          );
        }
        
        return (
          <Chip
            icon={<SecurityIcon />}
            label={t('admin:plans.table.safeToEdit')}
            size="small"
            color="success"
            variant="outlined"
          />
        );
      }
    },
    {
      key: 'isActive',
      title: t('common:labels.status'),
      sortable: true,
      render: (value, row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <StatusBadge status={row.isActive ? 'success' : 'default'}>
            {row.isActive ? t('common:status.active') : t('common:status.inactive')}
          </StatusBadge>
          {row.allowInstantNextPlanActivation && (
            <Chip
              label="Instant Activation"
              size="small"
              color="secondary"
              sx={{ fontSize: '0.7rem', height: '20px' }}
            />
          )}
          {row.freezable && (
            <Chip
              label="Freezable"
              size="small"
              color="info"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: '20px' }}
            />
          )}
        </Box>
      )
    }
  ];

  // Define table actions using useMemo to handle dynamic icons properly
  const actions = useMemo(() => ({
    row: [
      {
        icon: <EditIcon />, // Use a simple icon for now
        title: t('admin:plans.actions.edit'),
        onClick: handleEditPlan,
        variant: 'ghost'
      },
      {
        icon: <DeleteIcon />,
        title: t('admin:plans.actions.delete'),
        onClick: handleDeletePlan,
        variant: 'danger'
      }
    ]
  }), []);

  // Define empty state
  const emptyState = (
    <div>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>💳</div>
      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
        {t('admin:plans.emptyState.title')}
      </div>
      <div style={{ color: '#666' }}>
        {t('admin:plans.emptyState.description')}
      </div>
    </div>
  );

  if (!user || user.role !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('admin:access.adminRequired')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('admin:plans.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreatePlan}
        >
          {t('admin:plans.actions.create')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Data Table */}
      <DataTable
        data={plans}
        columns={columns}
        loading={loading}
        error={error}
        sorting={{ enabled: true }}
        filtering={{ enabled: true }}
        actions={actions}
        emptyState={emptyState}
      />

      {/* Plan Form Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {formMode === 'create' ? t('admin:plans.dialogs.createTitle') : t('admin:plans.dialogs.editTitle')}
        </DialogTitle>
        <DialogContent>
          <PlanForm
            plan={selectedPlan}
            onSubmit={handleFormSubmit}
            onCancel={() => setOpenForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>{t('admin:plans.dialogs.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('admin:plans.dialogs.deleteConfirmation', { name: selectedPlan?.name })}
            {t('common:messages.cannotUndo')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>{t('common:buttons.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common:buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan Impact Dialog */}
      <PlanImpactDialog
        open={openImpactDialog}
        onClose={() => setOpenImpactDialog(false)}
        planId={selectedPlan?._id}
        planName={selectedPlan?.name}
        title={t('admin:plans.dialogs.editWarning.title')}
        warningMessage={t('admin:plans.dialogs.editWarning.message')}
        showProceedButton={true}
        proceedButtonText={t('admin:plans.dialogs.editWarning.proceedButton')}
        onProceed={handleProceedWithEdit}
      />
    </Box>
  );
};

export default PlanListPage;